import * as React from 'react';
import '../styles/home.css';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FileUpload from '@cloudscape-design/components/file-upload';
import FormField from '@cloudscape-design/components/form-field';
import { useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Spinner from '@cloudscape-design/components/spinner';
import { PDFDocument } from 'pdf-lib';
import mergeicon from '../assets/mergefiles.PNG';
import Alert from '@cloudscape-design/components/alert';
import mammoth from 'mammoth';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { addUsage } from './add-usage.js';
import constants from '../constants.js';

const MergeFiles = () => {
  const [visible, setVisible] = useState(false);
  const [value, setValue] = useState([]);
  const [submitvalue, setSubmitvalue] = useState(['Merge']);
  const [servicevalue, setServicevalue] = useState([]);
  const [totalsize, setTotalsize] = useState([]);
  const [successnotify, setsuccessnotify] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [notify, setNotify] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState([]);
  const [fileCount, setFileCount] = useState(0); // New state for tracking file count

  /* The `toggleModal` function is a React hook `useCallback` that returns a memoized callback function.
When this callback function is invoked, it sets various states related to the modal for merging
PDFs.*/
  const toggleModal = React.useCallback(
    (service) => () => {
      setVisible(true);
      setValue('');
      setServicevalue(service);
      setSubmitvalue('Merge');
      setTotalsize(0);
      setNotify('');
      setError('');
      setFileCount(0); // Reset file count when modal is opened
    },
    [],
  );
  /**
   * The `onClickFooterButton` function resets various values and sets the submit value to "Merge".
   */
  const onClickFooterButton = () => {
    setVisible(false);
    setValue('');
    setSubmitvalue('Merge');
    setTotalsize(0);
    setFileCount(0);
    setNotify('');
    setError('');
  };

  /**
   * The function `_handleInputChange` processes input changes, calculates the total size of selected
   * files, and sets a submit value based on the total size.
   * @param e - The parameter `e` in the `_handleInputChange` function is an event object that contains
   * information about the input change event that triggered the function. It seems to have a `detail`
   * property that contains the value of the input change event.
   */
  async function _handleInputChange(e) {
    setNotify('');
    setError(null);
    setValue(e.detail.value);
    let fileslist = e.detail.value;
    let valueAdded = 0;
    let count = 0;
    for (const file of fileslist) {
      count = file.size;
      valueAdded += count;
    }
    setTotalsize(valueAdded / 1000000);
    if (totalsize > 2000) {
      setSubmitvalue('Limit Exceeded');
    } else if (totalsize <= 0) {
      setSubmitvalue('Merge');
    }
    setSelectedFiles(fileslist);
    setFileCount(fileslist.length); // Update file count when files are selected
    setSubmitvalue('Merge');
    setMessage('* select more than one file.');
  }

  /**
   * The function `Merging` merges multiple files(Images,Word documents, PDFs) into one and displays a success message with known
   * issues/limitations.
   */
  const Merging = async () => {
    setSubmitvalue(<Spinner />);
    if (selectedFiles.length < 1) {
      setNotify('Please select more than one file');
    }
    const pdfDoc = await PDFDocument.create();
    // Define the maximum width and height for the PDF pages
    const maxWidth = 612; // Standard PDF page width in points (8.5 inches)
    const maxHeight = 792; // Standard PDF page height in points (11 inches)

    for (const file of selectedFiles) {
      if (file.type === constants.pdfFileType) {
        const existingPdfBytes = await file.arrayBuffer();
        try {
          const existingPdfDoc = await PDFDocument.load(existingPdfBytes);
          const copiedPages = await pdfDoc.copyPages(existingPdfDoc, existingPdfDoc.getPageIndices());
          copiedPages.forEach((page) => pdfDoc.addPage(page));
        } catch (e) {
          try {
            const { handleEncryptedPDF } = await import('./pdf-encryption-handler.js');
            const processedPdfBytes = await handleEncryptedPDF(file);
            const existingPdfDoc = await PDFDocument.load(processedPdfBytes);
            const copiedPages = await pdfDoc.copyPages(existingPdfDoc, existingPdfDoc.getPageIndices());
            copiedPages.forEach((page) => pdfDoc.addPage(page));
          } catch (encryptionError) {
            setNotify(`"${file.name}" could not be processed. Please check if it's a valid PDF.`);
            console.error(`Error processing ${file.name}:`, encryptionError);
            setError('error');
            setSubmitvalue('Merge');
            return;
          }
        }
      } else if (file.type === 'image/jpeg' || file.type === 'image/png') {
        const imageBytes = await file.arrayBuffer();
        let image;
        if (file.type === 'image/png') {
          image = await pdfDoc.embedPng(imageBytes);
        } else if (file.type === 'image/jpeg') {
          image = await pdfDoc.embedJpg(imageBytes);
        }
        const page = pdfDoc.addPage([maxWidth, maxHeight]);
        const { width, height } = resizeToFit(image.width, image.height, maxWidth, maxHeight);
        const x = (maxWidth - width) / 2;
        const y = (maxHeight - height) / 2;
        page.drawImage(image, {
          x,
          y,
          width,
          height,
        });
      } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        try {
          const wordHtml = await convertWordToHtml(file);
          const wordPdf = await convertHtmlToPdf(wordHtml);
          const copiedPages = await pdfDoc.copyPages(wordPdf, wordPdf.getPageIndices());
          copiedPages.forEach((page) => pdfDoc.addPage(page));
        } catch (error) {
          console.error('Error processing Word document:', error);
          setNotify('Failed to combine files');
          setError('error');
          setSubmitvalue('Merge');
          return;
        }
      } else {
        setNotify('Unsupported file type. Please upload only images, docx, and pdfs');
        setError('error');
      }
    }

    // Download the merged PDF
    const mergedPdfBytes = await pdfDoc.save();
    const mergedPdfBlob = new Blob([mergedPdfBytes], { type: constants.pdfFileType });
    const url = URL.createObjectURL(mergedPdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'awesomepdf_combined.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    addUsage('AWESOME PDF TOOLS', '1.0', fileCount, window.location.href);

    setSubmitvalue('Success');
    if (error) {
      setVisible(true);
    } else {
      setVisible(false);
    }
    setsuccessnotify(
      <Alert statusIconAriaLabel="Info" type="success" header="Known issues/limitations">
        Review the documentation to learn about potential compatibility issues with specific database versions.
      </Alert>,
    );
    successnotify;
  };

  const resizeToFit = (width, height, maxWidth, maxHeight) => {
    const aspectRatio = width / height;

    // Calculate dimensions while preserving aspect ratio
    let newWidth = maxWidth;
    let newHeight = maxHeight;

    if (width / height > maxWidth / maxHeight) {
      newWidth = Math.min(width, maxWidth);
      newHeight = newWidth / aspectRatio;
    } else {
      newHeight = Math.min(height, maxHeight);
      newWidth = newHeight * aspectRatio;
    }

    return { width: newWidth, height: newHeight };
  };

  /**
   * The function `convertWordToHtml` converts Word document to HTML file.
   */
  const convertWordToHtml = async (wordFile) => {
    const arrayBuffer = await wordFile.arrayBuffer();
    const container = document.createElement('div');
    container.className = 'word-document-container';
    container.style.position = 'absolute';
    container.style.left = '0';
    container.style.top = '0';
    container.style.width = '800px';
    container.style.height = 'auto';
    container.style.opacity = '0.01';
    container.style.pointerEvents = 'none';
    document.body.appendChild(container);

    try {
      // Apply basic container styles without overriding colors
      container.style.fontFamily = 'Calibri, Arial, sans-serif';
      container.style.lineHeight = '1.5';
      container.style.margin = '0';
      container.style.padding = '40px';
      container.style.border = 'none';
      container.style.maxWidth = '720px';
      container.style.boxSizing = 'border-box';

      const style = document.createElement('style');
      style.textContent = `
        .docxContainer-wrapper { background: white !important; }
      `;
      document.head.appendChild(style);

      const result = await mammoth.convertToHtml({
        arrayBuffer,
        styleMap: [
          "p[style-name='Heading 1'] => h1:fresh",
          "p[style-name='Heading 2'] => h2:fresh",
          "p[style-name='Heading 3'] => h3:fresh",
          'table => table.preserve-colors',
          'tr => tr.preserve-colors',
          'td => td.preserve-colors',
          'th => th.preserve-colors',
        ],
        includeEmbeddedStyleMap: true,
        convertImage: mammoth.images.imgElement(function (image) {
          return image.read('base64').then(function (imageBuffer) {
            return {
              src: 'data:' + image.contentType + ';base64,' + imageBuffer,
            };
          });
        }),
      });

      container.innerHTML = result.value;

      // Handle wide tables with content-based column widths
      const tables = container.querySelectorAll('table');
      tables.forEach((table) => {
        table.style.borderCollapse = 'collapse';
        table.style.margin = '1em 0';
        table.style.width = '100%';
        table.style.tableLayout = 'auto';

        // Check for any existing background colors in the HTML
        const tableCells = table.querySelectorAll('td, th');

        // Apply basic styling since DOCX background colors aren't preserved
        const rows = table.querySelectorAll('tr');
        rows.forEach((row, rowIndex) => {
          const cells = row.querySelectorAll('td, th');

          // Style header row (first row with bold content)
          const hasStrongContent = row.querySelector('strong, em');
          if (rowIndex === 1 && hasStrongContent) {
            // Row 1 appears to be the header
            cells.forEach((cell) => {
              cell.style.backgroundColor = '#f0f0f0 !important';
              cell.style.fontWeight = 'bold';
            });
          }

          // Alternate row colors for better readability
          else if (rowIndex > 1 && rowIndex % 2 === 0) {
            cells.forEach((cell) => {
              cell.style.backgroundColor = '#f9f9f9 !important';
            });
          }
        });

        // Get max column count from all rows
        let maxColumns = 0;
        for (let row of table.rows) {
          maxColumns = Math.max(maxColumns, row.cells.length);
        }

        // Calculate content-based widths
        const columnWidths = [];
        for (let colIndex = 0; colIndex < maxColumns; colIndex++) {
          let maxContentLength = 0;
          for (let row of table.rows) {
            if (row.cells[colIndex]) {
              const content = row.cells[colIndex].textContent || '';
              maxContentLength = Math.max(maxContentLength, content.length);
            }
          }
          columnWidths.push(maxContentLength);
        }

        const totalContentLength = columnWidths.reduce((sum, length) => sum + length, 0);

        const cells = table.querySelectorAll('td, th');

        // Apply responsive styling based on column count
        if (maxColumns > 5) {
          table.style.fontSize = '9px';

          // Assign widths based on content length
          for (let row of table.rows) {
            for (let colIndex = 0; colIndex < row.cells.length; colIndex++) {
              const cell = row.cells[colIndex];
              const contentRatio = columnWidths[colIndex] / totalContentLength;
              const width = Math.max(contentRatio * 100, 100 / maxColumns); // Minimum equal width

              cell.style.border = '1px solid #000';
              cell.style.padding = '2px';
              cell.style.fontSize = '8px';
              cell.style.wordWrap = 'break-word';
              cell.style.width = `${width}%`;
              cell.style.minWidth = '0';
              cell.style.overflow = 'hidden';
            }
          }
        } else {
          cells.forEach((cell) => {
            cell.style.border = '1px solid #000';
            cell.style.padding = '6px';
            cell.style.fontSize = '11px';
          });
        }
      });

      // Process images to maintain proportions but avoid page breaks
      const images = container.querySelectorAll('img');
      images.forEach((img) => {
        img.style.display = 'block';
        img.style.breakInside = 'avoid';
        if (!img.style.maxWidth) {
          img.style.maxWidth = '100%';
        }
        if (!img.style.height) {
          img.style.height = 'auto';
        }
      });
      return container;
    } catch (e) {
      if (container && document.body.contains(container)) {
        document.body.removeChild(container);
      }
      throw new Error('Failed to convert Word document to HTML');
    }
  };

  const convertHtmlToPdf = async (tempContainer) => {
    try {
      // Create temp PDF to analyze split image positions
      const tempPdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const tempDoc = await new Promise((resolve) => {
        tempPdf.html(tempContainer, {
          callback: async function (doc) {
            resolve(doc);
          },
          width: 170,
          windowWidth: 720,
          autoPaging: 'text',
          margin: [20, 20],
          html2canvas: {
            backgroundColor: '#ffffff',
            scrollX: 0,
            scrollY: 0,
            x: 0,
            y: 0,
          },
        });
      });

      const pageCount = tempDoc.getNumberOfPages();

      // Find which page each image appears on and detect splits
      const imageIdsPerPage = new Map();

      for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
        tempDoc.setPage(pageNum);
        const pageContent = tempDoc.internal.pages[pageNum];
        const contentStr = pageContent ? pageContent.join('') : '';

        // Extract image IDs from page content - try multiple patterns
        let imageIds = [];

        // Pattern 1: /I# Do format
        const pattern1 = contentStr.match(/\/I(\d+)\s+Do/g) || [];
        imageIds = imageIds.concat(pattern1.map((match) => match.match(/\/I(\d+)/)[1]));
        // Pattern 2: Look for image objects in PDF stream
        const pattern2 = contentStr.match(/obj.*?\/Type\s*\/XObject.*?\/Subtype\s*\/Image/gs) || [];
        imageIds = imageIds.concat(pattern2.map((_, index) => (index + 1).toString()));
        // Remove duplicates
        imageIds = [...new Set(imageIds)];

        if (imageIds.length > 0) {
          imageIdsPerPage.set(pageNum, imageIds);
        }
      }
      // Detect split images by finding same image ID on consecutive pages
      const splitImages = new Map();
      for (let pageNum = 1; pageNum < pageCount; pageNum++) {
        const currentPageIds = imageIdsPerPage.get(pageNum) || [];
        const nextPageIds = imageIdsPerPage.get(pageNum + 1) || [];

        // Check for common image IDs between consecutive pages
        const commonIds = currentPageIds.filter((id) => nextPageIds.includes(id));
        if (commonIds.length > 0) {
          commonIds.forEach((id) => {
            splitImages.set(id, pageNum);
          });
        }
      }

      // Hide split images from HTML
      const images = tempContainer.querySelectorAll('img');
      const splitImageData = [];
      images.forEach((img, index) => {
        const imageId = index.toString();
        if (splitImages.has(imageId)) {
          splitImageData.push({
            src: img.src,
            originalPage: splitImages.get(imageId),
          });
          img.style.visibility = 'hidden';
        }
      });
      return new Promise((resolve) => {
        const pdf = new jsPDF({
          orientation: 'p',
          unit: 'mm',
          format: 'a4',
          compress: true,
        });
        pdf.html(tempContainer, {
          callback: async function (doc) {
            const originalPageCount = doc.getNumberOfPages();

            // Add split images on new pages
            splitImageData.forEach((imgData) => {
              doc.addPage();
              doc.addImage(imgData.src, 'JPEG', 20, 30, 170, 170);
            });

            // Recalculate split image page numbers after adding
            const updatedSplitImagePages = [];
            splitImageData.forEach((imgData, index) => {
              const newPageNum = originalPageCount + index + 1;
              updatedSplitImagePages.push(newPageNum);
            });

            // Move content pages based on updated split image positions
            splitImageData.forEach((imgData, index) => {
              const updatedImagePageNum = updatedSplitImagePages[index];
              const startPage = imgData.originalPage + 1;
              const endPage = updatedImagePageNum - 1;

              for (let p = startPage; p <= endPage; p++) {
                doc = doc.movePage(startPage, updatedImagePageNum);
              }

              // Find proper page number of split image ID after moving
              const splitImageId = [...splitImages.keys()][index + 1];
              let newImagePageNum = null;
              for (let pageNum = 1; pageNum <= doc.getNumberOfPages(); pageNum++) {
                doc.setPage(pageNum);
                const pageContent = doc.internal.pages[pageNum]?.join('') || '';
                if (pageContent.includes(`/I${parseInt(splitImageId) + 1}`)) {
                  newImagePageNum = pageNum;
                  break;
                }
              }

              if (newImagePageNum) {
                updatedSplitImagePages[index] = newImagePageNum;
                splitImageData[index + 1].originalPage = newImagePageNum;
              }
            });

            const pdfBytes = doc.output('arraybuffer');
            const pdfDoc = await PDFDocument.load(pdfBytes);
            resolve(pdfDoc);
          },
          width: 170,
          windowWidth: 720,
          autoPaging: 'text',
          margin: [20, 20],
          html2canvas: {
            backgroundColor: '#ffffff',
            scrollX: 0,
            scrollY: 0,
            x: 0,
            y: 0,
          },
        });
      });
    } finally {
      document.body.removeChild(tempContainer);
    }
  };

  return (
    <div>
      <div className="service-card" onClick={toggleModal('Combine Files')}>
        <div className="service-card-icon">
          <img src={mergeicon} alt="Combine Files" loading="lazy" />
        </div>
        <h3 className="service-card-title">Combine Files</h3>
        <p className="service-card-desc">Combine files (IMG, DOCX & PDF) into one single PDF</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={() => {
          setVisible(false);
        }}
        visible={visible}
        size="small"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onClickFooterButton}>
                Cancel
              </Button>
              <div>
                {totalsize < 2000 && totalsize > 0 && selectedFiles.length > 1 && !error ? (
                  <div>
                    <Button variant="primary" onClick={Merging}>
                      {submitvalue}
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Button disabled variant="primary">
                      {submitvalue}
                    </Button>
                  </div>
                )}
              </div>
            </SpaceBetween>
          </Box>
        }
        header={servicevalue}
      >
        {servicevalue === 'Combine Files' ? (
          <div>
            <FormField
              errorText={notify}
              label="Upload files"
              description="Only images(jpg & png), docx, PDF format is supported"
            >
              <FileUpload
                onChange={_handleInputChange}
                value={value}
                i18nStrings={{
                  uploadButtonText: (e) => (e ? 'Choose files' : 'Choose'),
                  dropzoneText: (e) => (e ? 'Drop files to upload' : 'Drop file to upload'),
                  removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
                  limitShowFewer: 'Show fewer files',
                  limitShowMore: 'Show more files',
                  errorIconAriaLabel: 'Error',
                }}
                multiple
                showFileLastModified
                showFileSize
                showFileThumbnail
                tokenLimit={9}
                constraintText=""
                accept=".pdf,.docx,.png,.jpg,.jpeg"
              />
            </FormField>
          </div>
        ) : (
          <div></div>
        )}
        <br />
        Total maximum limit: 2GB
        <div>{selectedFiles.length < 2 ? <p style={{ color: '#eb5834' }}>{message}</p> : null}</div>
      </Modal>
    </div>
  );
};
export default MergeFiles;
